import React from 'react';
import {Modal} from '@mantine/core';
import {Anchor} from 'src/design/atoms/Anchor';
import {List} from 'src/design/atoms/List';
import {Text} from 'src/design/atoms/Text';
import {Link} from 'react-router-dom';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {useGetCourseClassroomCloseoutPreviewQuery} from 'src/redux/queries/courseClassroom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface CloseoutReviewDialogProps {
  opened: boolean;
  onClose: () => void;
  classroomId: number;
  isSaving: boolean;
  onConfirm: () => void;
  /** Seeds the Oversight tab's search box when linking out — the server
   * only supports a free-text search (trainee/skill name), not a true
   * class/session filter, so this is a best-effort narrowing, not an exact
   * one. */
  courseName?: string;
}

/**
 * Close-out review dialog — fed by the server-computed preview, replacing
 * the bare all-or-nothing acknowledgement. What confirming runs is the
 * caller's business: the `complete-and-closeout` transition from In Progress,
 * or the late close-out endpoint for a class already Completed.
 */
export const CloseoutReviewDialog: React.FC<CloseoutReviewDialogProps> = ({
  opened,
  onClose,
  classroomId,
  isSaving,
  onConfirm,
  courseName,
}) => {
  const {data: preview, isLoading} = useGetCourseClassroomCloseoutPreviewQuery(
    classroomId,
    {skip: !opened}
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Review & Close Out"
      centered>
      {isLoading || !preview ? (
        <Spinner loadingText="Loading preview...." />
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="md">
            This will sign off training for every attendee marked Present,
            based on their recorded result. It does not affect attendees who
            are Absent or Excused.
          </Text>
          <List spacing="xs" size="sm" mb="lg">
            <List.Item>
              <Text span fw={600} c="green">
                {preview.willCompleteCount}
              </Text>{' '}
              will be marked Complete
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                {preview.staysOpenCount}
              </Text>{' '}
              absent/excused stay Open
            </List.Item>
            <List.Item>
              <Text span fw={600}>
                {preview.alreadyCompletedCount}
              </Text>{' '}
              already completed (skipped)
            </List.Item>
            {preview.alreadySignedOffCount > 0 && (
              <List.Item>
                <Text span fw={600}>
                  {preview.alreadySignedOffCount}
                </Text>{' '}
                already signed off by this class (left untouched)
              </List.Item>
            )}
            {preview.failedCount > 0 && (
              <List.Item>
                <Text span fw={600} c="red">
                  {preview.failedCount}
                </Text>{' '}
                marked Failed
              </List.Item>
            )}
            {preview.demonstrationsPendingCount > 0 && (
              <List.Item>
                <Text span fw={600} c="yellow">
                  {preview.demonstrationsPendingCount}
                </Text>{' '}
                demonstrations pending sign-off
                {courseName && (
                  <>
                    {' '}
                    (
                    <Anchor
                      component={Link}
                      to={`${PathUtility.TrainingSignOffs()}?tab=oversight&courseName=${encodeURIComponent(
                        courseName
                      )}`}
                      target="_blank"
                      size="sm">
                      view in Oversight
                    </Anchor>
                    )
                  </>
                )}
              </List.Item>
            )}
          </List>
        </>
      )}
      <Button
        fullWidth
        color="green"
        onClick={onConfirm}
        loading={isSaving}
        disabled={isLoading || !preview}>
        Confirm Close-Out
      </Button>
    </Modal>
  );
};
